import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import PublicidadModal from "../components/PublicidadModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  FileText, 
  Plus, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  Trash2,
  Eye,
  Search,
  Filter,
  Calendar,
  X,
  TrendingUp,
  Timer,
  BarChart3,
  ClipboardCheck,
  DollarSign,
  Euro,
  RefreshCw,
  CalendarDays,
  Target,
  Activity,
  Hourglass
} from "lucide-react";
import { ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Legend } from "recharts";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function DashboardPage() {
  const [comparisons, setComparisons] = useState([]);
  const [filteredComparisons, setFilteredComparisons] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [tasas, setTasas] = useState(null);
  const [loadingTasas, setLoadingTasas] = useState(true);
  const [statsInfo, setStatsInfo] = useState(null);
  const [statsPeriodo, setStatsPeriodo] = useState("all");
  const [customDesde, setCustomDesde] = useState("");
  const [customHasta, setCustomHasta] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetDesde, setResetDesde] = useState("");
  const [resetHasta, setResetHasta] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreDesde, setRestoreDesde] = useState("");
  const [restoreHasta, setRestoreHasta] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  
  const canCompare = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'administrador' || user?.menu_permissions?.compare?.access === true;
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    fetchTasas();
  }, []);

  useEffect(() => {
    filterComparisons();
  }, [comparisons, searchQuery, statusFilter, dateFilter]);

  const fetchData = async () => {
    try {
      const [compRes, docRes, repRes, statsRes] = await Promise.all([
        axios.get(`${API}/comparisons`, getAuthHeaders()),
        axios.get(`${API}/documents`, getAuthHeaders()),
        axios.get(`${API}/reports`, getAuthHeaders()),
        axios.get(`${API}/comparisons/estadisticas`, getAuthHeaders()).catch(() => null)
      ]);
      setComparisons(compRes.data);
      setFilteredComparisons(compRes.data);
      setDocuments(docRes.data);
      setReports(repRes.data);
      if (statsRes) setStatsInfo(statsRes.data);
    } catch (err) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasas = async () => {
    try {
      setLoadingTasas(true);
      const response = await axios.get(`${API}/tasas`, getAuthHeaders());
      setTasas(response.data);
    } catch (err) {
      console.error("Error al cargar tasas:", err);
    } finally {
      setLoadingTasas(false);
    }
  };

  const isAdminUser = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const reiniciarEstadisticas = async () => {
    if (!resetDesde || !resetHasta) { toast.error("Indica las fechas desde y hasta"); return; }
    if (resetHasta < resetDesde) { toast.error("La fecha hasta no puede ser menor que desde"); return; }
    setResetLoading(true);
    try {
      const res = await axios.post(`${API}/comparisons/estadisticas/reiniciar`,
        { desde: resetDesde, hasta: resetHasta }, getAuthHeaders());
      toast.success(`Estadísticas reiniciadas: ${res.data.excluidas} comparaciones excluidas del período`);
      setResetOpen(false);
      setResetDesde(""); setResetHasta("");
      const statsRes = await axios.get(`${API}/comparisons/estadisticas`, getAuthHeaders());
      setStatsInfo(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reiniciar estadísticas");
    } finally { setResetLoading(false); }
  };

  const restaurarEstadisticas = async () => {
    if (!restoreDesde || !restoreHasta) { toast.error("Indica las fechas desde y hasta"); return; }
    if (restoreHasta < restoreDesde) { toast.error("La fecha hasta no puede ser menor que desde"); return; }
    setRestoreLoading(true);
    try {
      const res = await axios.post(`${API}/comparisons/estadisticas/restaurar`,
        { desde: restoreDesde, hasta: restoreHasta }, getAuthHeaders());
      toast.success(`Estadísticas restauradas: ${res.data.restauradas} comparaciones devueltas`);
      setRestoreOpen(false);
      setRestoreDesde(""); setRestoreHasta("");
      const statsRes = await axios.get(`${API}/comparisons/estadisticas`, getAuthHeaders());
      setStatsInfo(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar estadísticas");
    } finally { setRestoreLoading(false); }
  };

  // Datos de estadísticas con jerarquía (endpoint) + filtro de período
  const getStatsItems = () => {
    const base = statsInfo?.items || comparisons;
    if (statsPeriodo === "all") return base;
    if (statsPeriodo === "custom") {
      if (!customDesde && !customHasta) return base;
      const d0 = customDesde ? new Date(customDesde + "T00:00:00") : null;
      const d1 = customHasta ? new Date(customHasta + "T23:59:59.999") : null;
      return base.filter(c => {
        const f = new Date(c.created_at);
        return (!d0 || f >= d0) && (!d1 || f <= d1);
      });
    }
    let cutoff;
    if (statsPeriodo === "este_anio") cutoff = new Date(new Date().getFullYear(), 0, 1);
    else if (statsPeriodo === "24h") cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    else cutoff = new Date(Date.now() - parseInt(statsPeriodo, 10) * 24 * 60 * 60 * 1000);
    return base.filter(c => new Date(c.created_at) >= cutoff);
  };

  // Calculate estimated time saved (5 min per comparison manually vs 30 sec with AI)
  const getTimeSaved = () => {
    const completedComparisons = getStatsItems().filter(c => c.status === "completed").length;
    const minutesSaved = completedComparisons * 4.5; // 5 min manual - 0.5 min AI = 4.5 min saved
    if (minutesSaved >= 60) {
      const hours = Math.floor(minutesSaved / 60);
      const mins = Math.round(minutesSaved % 60);
      return `${hours}h ${mins}m`;
    }
    return `${Math.round(minutesSaved)}m`;
  };

  // Get comparisons from last 7 days
  const getWeeklyGrowth = () => {
    const items = getStatsItems();
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const thisWeek = items.filter(c => new Date(c.created_at) >= weekAgo).length;
    const previousWeek = items.filter(c => {
      const date = new Date(c.created_at);
      const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
      return date >= twoWeeksAgo && date < weekAgo;
    }).length;
    
    if (previousWeek === 0) return thisWeek > 0 ? "+100%" : "0%";
    const growth = ((thisWeek - previousWeek) / previousWeek * 100).toFixed(0);
    return growth >= 0 ? `+${growth}%` : `${growth}%`;
  };

  // ── Estadísticas de decisión (calculadas de las comparaciones) ──
  const MIN_AHORRO_POR_COMP = 4.5;

  const formatMinutos = (m) => {
    if (m >= 60) return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
    return `${Math.round(m)}m`;
  };

  const getStatsDecision = () => {
    const items = getStatsItems();
    const ahora = new Date();
    const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
    const inicioMesAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
    const esteMes = items.filter(c => new Date(c.created_at) >= inicioMes).length;
    const mesAnterior = items.filter(c => {
      const d = new Date(c.created_at);
      return d >= inicioMesAnterior && d < inicioMes;
    }).length;
    let deltaMes = null;
    if (mesAnterior > 0) deltaMes = Math.round(((esteMes - mesAnterior) / mesAnterior) * 100);
    else if (esteMes > 0) deltaMes = 100;

    const completadas = items.filter(c => c.status === "completed").length;
    const errores = items.filter(c => c.status === "error").length;
    const tasaExito = (completadas + errores) > 0 ? Math.round((completadas / (completadas + errores)) * 100) : null;

    let promedioSemanal = null;
    let promedioDiario = null;
    const fechas = items.map(c => new Date(c.created_at)).filter(d => !isNaN(d));
    if (fechas.length > 0) {
      const primera = new Date(Math.min(...fechas));
      const semanas = Math.max(1, (ahora - primera) / (7 * 24 * 60 * 60 * 1000));
      promedioSemanal = (items.length / semanas).toFixed(1);
      const dias = Math.max(1, (ahora - primera) / (24 * 60 * 60 * 1000));
      promedioDiario = (items.length / dias).toFixed(1);
    }

    const completadasEsteMes = items.filter(c => c.status === "completed" && new Date(c.created_at) >= inicioMes).length;
    const ahorroEsteMes = completadasEsteMes * MIN_AHORRO_POR_COMP;
    const mesesActivos = new Set(items.filter(c => c.status === "completed").map(c => {
      const d = new Date(c.created_at);
      return `${d.getFullYear()}-${d.getMonth()}`;
    })).size;
    const ahorroPromedioMensual = mesesActivos > 0 ? (completadas * MIN_AHORRO_POR_COMP) / mesesActivos : 0;
    const ahorroTotal = completadas * MIN_AHORRO_POR_COMP;

    // Día de la semana más activo
    const diasSemana = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
    const porDia = {};
    fechas.forEach(d => { porDia[d.getDay()] = (porDia[d.getDay()] || 0) + 1; });
    const diaTop = Object.entries(porDia).sort((a, b) => b[1] - a[1])[0];
    const diaMasActivo = diaTop ? { nombre: diasSemana[diaTop[0]], total: diaTop[1] } : null;

    // Mejor mes histórico
    const mesesNombres = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const porMes = {};
    fechas.forEach(d => {
      const k = `${mesesNombres[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
      porMes[k] = (porMes[k] || 0) + 1;
    });
    const mesTop = Object.entries(porMes).sort((a, b) => b[1] - a[1])[0];
    const mejorMes = mesTop ? { nombre: mesTop[0], total: mesTop[1] } : null;

    // Usuario más activo (solo si hay visibilidad por jerarquía)
    let usuarioTop = null;
    if (statsInfo?.mostrar_por_usuario) {
      const porUsuario = {};
      items.forEach(c => { if (c.user_name) porUsuario[c.user_name] = (porUsuario[c.user_name] || 0) + 1; });
      const uTop = Object.entries(porUsuario).sort((a, b) => b[1] - a[1])[0];
      if (uTop) usuarioTop = { nombre: uTop[0], total: uTop[1] };
    }

    // Del día (hoy vs ayer) — independiente del filtro de período
    const base = statsInfo?.items || comparisons;
    const hoy0 = new Date(); hoy0.setHours(0, 0, 0, 0);
    const ayer0 = new Date(hoy0.getTime() - 24 * 60 * 60 * 1000);
    const comparacionesHoy = base.filter(c => new Date(c.created_at) >= hoy0).length;
    const comparacionesAyer = base.filter(c => {
      const f = new Date(c.created_at);
      return f >= ayer0 && f < hoy0;
    }).length;
    const repFechas = statsInfo?.reports_fechas || [];
    const informesHoy = repFechas.filter(f => new Date(f) >= hoy0).length;
    const informesAyer = repFechas.filter(fs => {
      const f = new Date(fs);
      return f >= ayer0 && f < hoy0;
    }).length;

    return { esteMes, mesAnterior, deltaMes, tasaExito, promedioSemanal, promedioDiario,
             ahorroEsteMes, ahorroPromedioMensual, ahorroTotal, diaMasActivo, mejorMes, usuarioTop,
             comparacionesHoy, comparacionesAyer, informesHoy, informesAyer };
  };

  // Serie mensual apilada por usuario (top 5 + Otros) — solo supervisores/admin
  const getTendenciaPorUsuario = () => {
    const items = getStatsItems();
    const conteo = {};
    items.forEach(c => { const n = c.user_name || "—"; conteo[n] = (conteo[n] || 0) + 1; });
    const top = Object.entries(conteo).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n]) => n);
    const hayOtros = Object.keys(conteo).length > top.length;
    const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const ahora = new Date();
    const serie = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      const fin = new Date(ahora.getFullYear(), ahora.getMonth() - i + 1, 1);
      const fila = { mes: `${meses[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` };
      top.forEach(n => { fila[n] = 0; });
      if (hayOtros) fila["Otros"] = 0;
      items.forEach(c => {
        const f = new Date(c.created_at);
        if (f >= d && f < fin) {
          const n = c.user_name || "—";
          if (top.includes(n)) fila[n] += 1;
          else if (hayOtros) fila["Otros"] += 1;
        }
      });
      serie.push(fila);
    }
    return { serie, claves: hayOtros ? [...top, "Otros"] : top };
  };

  const filterComparisons = () => {
    let filtered = [...comparisons];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.document1_name?.toLowerCase().includes(query) ||
        c.document2_name?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter(c => {
        const compareDate = new Date(c.created_at);
        switch (dateFilter) {
          case "today":
            return compareDate.toDateString() === now.toDateString();
          case "week":
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return compareDate >= weekAgo;
          case "month":
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return compareDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredComparisons(filtered);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateFilter("all");
  };

  const hasActiveFilters = searchQuery || statusFilter !== "all" || dateFilter !== "all";

  const deleteComparison = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta comparación?")) return;
    try {
      await axios.delete(`${API}/comparisons/${id}`, getAuthHeaders());
      toast.success("Comparación eliminada");
      setComparisons(comparisons.filter(c => c.id !== id));
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <span className="status-completed">
            <CheckCircle2 className="w-3 h-3" />
            Completado
          </span>
        );
      case "processing":
        return (
          <span className="status-processing">
            <Loader2 className="w-3 h-3 animate-spin" />
            Procesando
          </span>
        );
      case "error":
        return (
          <span className="status-error">
            <AlertCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <PublicidadModal />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Bienvenido, {user?.name}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Panel de control de comparaciones de pólizas</p>
          </div>
          {canCompare && (
            <Link to="/compare">
              <Button className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white w-full sm:w-auto" data-testid="new-comparison-button">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Comparación
              </Button>
            </Link>
          )}
        </div>

        {/* Tarjetas de Tasas de Cambio */}
        <div className="flex flex-wrap gap-4 mb-6">
          {/* Dólar */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-green-600 to-green-700 text-white w-auto">
            <CardContent className="p-3 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <DollarSign className="h-4 w-4" />
                </div>
                <span className="font-semibold text-sm">USD</span>
              </div>
              {loadingTasas ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : tasas?.monedas?.USD ? (
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-green-200 text-xs">C:</span>
                    <span className="font-bold">{tasas.monedas.USD.compra.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-green-200 text-xs">V:</span>
                    <span className="font-bold">{tasas.monedas.USD.venta.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-green-200">N/D</span>
              )}
              <button 
                onClick={fetchTasas}
                className="p-1 bg-white/20 rounded-full hover:bg-white/30 transition-colors ml-1"
                title="Actualizar"
              >
                <RefreshCw className={`h-3 w-3 ${loadingTasas ? 'animate-spin' : ''}`} />
              </button>
            </CardContent>
          </Card>

          {/* Euro */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-purple-600 to-purple-700 text-white w-auto">
            <CardContent className="p-3 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Euro className="h-4 w-4" />
                </div>
                <span className="font-semibold text-sm">EUR</span>
              </div>
              {loadingTasas ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : tasas?.monedas?.EUR ? (
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-purple-200 text-xs">C:</span>
                    <span className="font-bold">{tasas.monedas.EUR.compra.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-purple-200 text-xs">V:</span>
                    <span className="font-bold">{tasas.monedas.EUR.venta.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-purple-200">N/D</span>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filtro de período para estadísticas */}
        <div className="flex items-center justify-end gap-2 mb-3" data-testid="stats-periodo-filtro">
          {isAdminUser && (
            <div className="flex items-center gap-2 mr-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-9 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setResetOpen(true)}
                data-testid="reiniciar-stats-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Reiniciar estadísticas
              </Button>
              {(statsInfo?.excluidas_count || 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                  onClick={() => setRestoreOpen(true)}
                  data-testid="restaurar-stats-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5 rotate-180" />
                  Restaurar estadísticas ({statsInfo.excluidas_count})
                </Button>
              )}
            </div>
          )}
          <CalendarDays className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm text-slate-500 dark:text-slate-400">Estadísticas:</span>
          <Select value={statsPeriodo} onValueChange={setStatsPeriodo}>
            <SelectTrigger className="w-44 h-9" data-testid="stats-periodo-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el tiempo</SelectItem>
              <SelectItem value="24h">Últimas 24 horas</SelectItem>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="180">Últimos 6 meses</SelectItem>
              <SelectItem value="365">Últimos 12 meses</SelectItem>
              <SelectItem value="este_anio">Este año</SelectItem>
              <SelectItem value="custom">Personalizado…</SelectItem>
            </SelectContent>
          </Select>
          {statsPeriodo === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="stats-custom-fechas">
              <Input type="date" className="h-9 w-36" value={customDesde} onChange={(e) => setCustomDesde(e.target.value)} data-testid="stats-custom-desde" />
              <span className="text-sm text-slate-500">a</span>
              <Input type="date" className="h-9 w-36" value={customHasta} onChange={(e) => setCustomHasta(e.target.value)} data-testid="stats-custom-hasta" />
            </div>
          )}
        </div>

        {/* Stats Cards - Clickable filters */}
        <div className="kpi-carousel grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card 
            className={`border-slate-200 shadow-sm bg-gradient-to-br from-[#0C314C] to-[#2a4d7a] cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "all" && !searchQuery ? "ring-2 ring-[#45E69A]" : ""}`}
            onClick={() => { setStatusFilter("all"); setSearchQuery(""); setDateFilter("all"); }}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-200">Total Comparaciones</CardTitle>
              <BarChart3 className="w-4 h-4 text-[#45E69A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{getStatsItems().length}</div>
              <p className="text-xs text-slate-300 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-[#45E69A]" />
                {getWeeklyGrowth()} esta semana
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Informes Generados</CardTitle>
              <ClipboardCheck className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">{statsInfo ? statsInfo.reports_count : reports.length}</div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Informes ejecutivos</p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Documentos</CardTitle>
              <FileText className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">{statsInfo ? statsInfo.documents_count : documents.length}</div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Pólizas subidas</p>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-[#45E69A] to-[#8ab82a]">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#0C314C]">Tiempo Ahorrado</CardTitle>
              <Timer className="w-4 h-4 text-[#0C314C]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#0C314C]">{getTimeSaved()}</div>
              <p className="text-xs text-[#0C314C]/70 mt-1">vs análisis manual</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row - Clickable filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div 
            className={`flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "completed" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => { setStatusFilter("completed"); setSearchQuery(""); }}
          >
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {getStatsItems().filter(c => c.status === "completed").length}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Completadas</p>
            </div>
          </div>
          
          <div 
            className={`flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "processing" ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => { setStatusFilter("processing"); setSearchQuery(""); }}
          >
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {getStatsItems().filter(c => c.status === "processing").length}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">En proceso</p>
            </div>
          </div>
          
          <div 
            className={`flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "error" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => { setStatusFilter("error"); setSearchQuery(""); }}
          >
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {getStatsItems().filter(c => c.status === "error").length}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Con errores</p>
            </div>
          </div>
        </div>

        {/* Estadísticas para toma de decisiones */}
        {(() => {
          const sd = getStatsDecision();
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-decision-row">
              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 border-t-4 border-t-[#45E69A]" data-testid="stat-comparaciones-hoy">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Comparaciones de hoy</CardTitle>
                  <Activity className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{sd.comparacionesHoy}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">ayer: {sd.comparacionesAyer}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 border-t-4 border-t-[#45E69A]" data-testid="stat-informes-hoy">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Informes de hoy</CardTitle>
                  <ClipboardCheck className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{sd.informesHoy}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">ayer: {sd.informesAyer}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-comparaciones-mes">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Comparaciones este mes</CardTitle>
                  <CalendarDays className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{sd.esteMes}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {sd.deltaMes === null ? "sin datos del mes anterior" : (
                      <span className={sd.deltaMes >= 0 ? "text-green-600 dark:text-[#45E69A]" : "text-red-500"}>
                        {sd.deltaMes >= 0 ? "+" : ""}{sd.deltaMes}% vs mes anterior ({sd.mesAnterior})
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-promedio-semanal">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Promedio semanal</CardTitle>
                  <Activity className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{sd.promedioSemanal ?? "—"}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">comparaciones por semana</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-tasa-exito">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Tasa de éxito</CardTitle>
                  <Target className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className={`text-3xl font-bold ${sd.tasaExito === null ? "text-slate-400" : sd.tasaExito >= 80 ? "text-green-600 dark:text-[#45E69A]" : sd.tasaExito >= 50 ? "text-amber-500" : "text-red-500"}`}>
                    {sd.tasaExito === null ? "—" : `${sd.tasaExito}%`}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">completadas vs con errores</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-ahorro-promedio">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Ahorro promedio mensual</CardTitle>
                  <Hourglass className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{formatMinutos(sd.ahorroPromedioMensual)}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">este mes: {formatMinutos(sd.ahorroEsteMes)} ahorrados</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-promedio-diario">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Promedio diario</CardTitle>
                  <Clock className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{sd.promedioDiario ?? "—"}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">comparaciones por día</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-dia-activo">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Día más activo</CardTitle>
                  <Calendar className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white capitalize">{sd.diaMasActivo ? sd.diaMasActivo.nombre : "—"}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sd.diaMasActivo ? `${sd.diaMasActivo.total} comparaciones` : "sin datos"}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-mejor-mes">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Mejor mes</CardTitle>
                  <BarChart3 className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">{sd.mejorMes ? sd.mejorMes.nombre : "—"}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sd.mejorMes ? `${sd.mejorMes.total} comparaciones` : "sin datos"}</p>
                </CardContent>
              </Card>

              {statsInfo?.mostrar_por_usuario && (
                <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800" data-testid="stat-usuario-top">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Usuario más activo</CardTitle>
                    <Target className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold text-slate-900 dark:text-white truncate">{sd.usuarioTop ? sd.usuarioTop.nombre : "—"}</div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sd.usuarioTop ? `${sd.usuarioTop.total} comparaciones` : "sin datos"}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          );
        })()}

        {/* Gráfica de tendencia mensual por usuario — solo admin y supervisores */}
        {statsInfo?.mostrar_por_usuario && (
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-8 dark:bg-slate-800" data-testid="grafica-tendencia-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#0C314C] dark:text-[#45E69A]" />
              Tendencia de comparaciones por mes y por usuario (últimos 12 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56 w-full" data-testid="grafica-tendencia">
              {(() => {
                const { serie, claves } = getTendenciaPorUsuario();
                const colores = ["#0C314C", "#45E69A", "#2a6f97", "#f59e0b", "#8b5cf6", "#94a3b8"];
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={serie} margin={{ top: 10, right: 16, left: -14, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#94a3b833" />
                      <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(12,49,76,0.15)", fontSize: "12px" }}
                        labelStyle={{ color: "#0C314C", fontWeight: 700 }}
                      />
                      <Legend wrapperStyle={{ fontSize: "11px" }} />
                      {claves.map((k, i) => (
                        <Bar key={k} dataKey={k} stackId="usuarios" fill={colores[i % colores.length]} radius={i === claves.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Diálogo de confirmación para reiniciar estadísticas (solo admin) */}
        <Dialog open={resetOpen} onOpenChange={setResetOpen}>
          <DialogContent className="sm:max-w-md" data-testid="reiniciar-stats-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Reiniciar estadísticas
              </DialogTitle>
              <DialogDescription>
                Las comparaciones del período seleccionado se <strong>excluirán de las estadísticas y de la gráfica</strong> para
                todos los usuarios. No se borran las comparaciones ni sus informes; la lista de comparaciones recientes no cambia.
                Esta acción no se puede deshacer desde la pantalla.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Desde</label>
                <Input type="date" value={resetDesde} onChange={(e) => setResetDesde(e.target.value)} data-testid="reiniciar-desde-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Hasta</label>
                <Input type="date" value={resetHasta} onChange={(e) => setResetHasta(e.target.value)} data-testid="reiniciar-hasta-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="reiniciar-cancelar-btn">Cancelar</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={resetLoading}
                onClick={reiniciarEstadisticas}
                data-testid="reiniciar-confirmar-btn"
              >
                {resetLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                Sí, reiniciar estadísticas
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo de confirmación para restaurar estadísticas (solo admin) */}
        <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
          <DialogContent className="sm:max-w-md" data-testid="restaurar-stats-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Restaurar estadísticas
              </DialogTitle>
              <DialogDescription>
                Las comparaciones excluidas del período seleccionado <strong>volverán a contarse en las estadísticas y en la gráfica</strong>.
                Actualmente hay <strong>{statsInfo?.excluidas_count || 0}</strong> comparaciones excluidas en total.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Desde</label>
                <Input type="date" value={restoreDesde} onChange={(e) => setRestoreDesde(e.target.value)} data-testid="restaurar-desde-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">Hasta</label>
                <Input type="date" value={restoreHasta} onChange={(e) => setRestoreHasta(e.target.value)} data-testid="restaurar-hasta-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreOpen(false)} data-testid="restaurar-cancelar-btn">Cancelar</Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={restoreLoading}
                onClick={restaurarEstadisticas}
                data-testid="restaurar-confirmar-btn"
              >
                {restoreLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Sí, restaurar estadísticas
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Search and Filters */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre de documento..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-slate-950"
                  data-testid="search-input"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px] border-slate-200" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="completed">Completado</SelectItem>
                  <SelectItem value="processing">Procesando</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full md:w-[180px] border-slate-200" data-testid="date-filter">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="week">Última semana</SelectItem>
                  <SelectItem value="month">Último mes</SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="border-slate-200"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </div>

            {/* Results Count */}
            {hasActiveFilters && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
                Mostrando {filteredComparisons.length} de {comparisons.length} comparaciones
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Comparisons */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
                Comparaciones {hasActiveFilters ? "Filtradas" : "Recientes"}
              </CardTitle>
              <Clock className="w-4 h-4 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredComparisons.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  {hasActiveFilters 
                    ? "No se encontraron comparaciones con los filtros seleccionados"
                    : "No tienes comparaciones aún"
                  }
                </p>
                {hasActiveFilters ? (
                  <Button variant="outline" onClick={clearFilters} className="border-slate-200">
                    Limpiar filtros
                  </Button>
                ) : canCompare ? (
                  <Link to="/compare">
                    <Button variant="outline" className="border-slate-200" data-testid="empty-state-new-comparison">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear primera comparación
                    </Button>
                  </Link>
                ) : null}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredComparisons.map((comparison) => (
                  <div
                    key={comparison.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`comparison-row-${comparison.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {comparison.document1_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            vs {comparison.document2_name}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden md:block">
                        <p className="text-xs text-slate-500">{formatDate(comparison.created_at)}</p>
                      </div>
                      {getStatusBadge(comparison.status)}
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/comparison/${comparison.id}`)}
                          className="text-slate-600 hover:text-slate-900"
                          data-testid={`view-comparison-${comparison.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteComparison(comparison.id)}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`delete-comparison-${comparison.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
